import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { requestTierUpgrade } from 'src/components/property/UtilityPortalCredentials/requestBillAutomationUpgrade';
import { SUBSCRIPTION_FORM_URL } from 'src/constants/billAutomation';
import { useStore } from 'src/stores';
import { noteUpgradeRequested } from 'src/stores/company/upgradeRequests';
import { tierOf } from './tiers';
import useStyles from './styles';

/**
 * "Upgrade now"'s terms (LB-6187, PRD V3 FR-5): a plan covers the whole portfolio (Dan, 20 Oct), so the request goes
 * only once the property manager agrees to that. Sending files the subscription form's upgrade request naming the plan;
 * the team then updates the subscription by hand, so it says nothing has changed yet.
 *
 * `companyId` is whose request it is, remembered once it's sent; `where` names the screen it was asked from.
 */
export const UpgradeDialog = observer(_ref => {
  let tier = _ref.tier,
    where = _ref.where,
    companyId = _ref.companyId,
    open = _ref.open,
    onClose = _ref.onClose;
  const classes = useStyles();
  const store = useStore();
  const entry = tierOf(tier);
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    agreed = _useState2[0],
    setAgreed = _useState2[1];
  const _useState3 = useState('idle'),
    _useState4 = _slicedToArray(_useState3, 2),
    state = _useState4[0],
    setState = _useState4[1]; // idle | sending | sent | failed
  // A second click before Send re-renders disabled would file the request twice.
  const sendingNow = useRef(false);
  useEffect(() => {
    if (!open) return;
    // Each opening asks again.
    setAgreed(false);
    setState('idle');
  }, [open]);
  if (!entry) return null;
  const sending = state === 'sending';
  const sent = state === 'sent';
  const handleClose = () => {
    if (sending) return;
    onClose === null || onClose === void 0 || onClose();
  };
  const handleSend = async () => {
    var _store$homeDashboardS;
    if (sendingNow.current || !agreed) return;
    sendingNow.current = true;
    setState('sending');
    // The company on screen is the one asked for; its name only when the store's current company is that one.
    const current = (_store$homeDashboardS = store.homeDashboardStore) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.getCurrentCompany;
    const name = current && String(current.id).toLowerCase() === String(companyId).toLowerCase() ? current.name : '';
    const accepted = await requestTierUpgrade(store.account.userProfile, entry.name, where, {
      id: companyId,
      name
    });
    sendingNow.current = false;
    if (accepted) noteUpgradeRequested(companyId, entry.name);
    setState(accepted ? 'sent' : 'failed');
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!open,
    handleClose: handleClose,
    variant: "middle",
    title: "Upgrade to ".concat(entry.name)
    // Once sent there's nothing to cancel: without this the modal shows its own Cancel beside Done.
    ,
    noLeftButton: sent,
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleClose,
      variant: "text",
      disabled: sending
    }, "Cancel"),
    rightButton: sent ? /*#__PURE__*/React.createElement(Button, {
      onClick: handleClose,
      size: "small",
      variant: "contained"
    }, "Done") : /*#__PURE__*/React.createElement(Button, {
      onClick: handleSend,
      size: "small",
      variant: "contained",
      disabled: !agreed || sending
    }, "Send request")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dialog,
    "data-testid": "upgrade-dialog"
  }, sent ? /*#__PURE__*/React.createElement(Alert, {
    severity: "success"
  }, "Request sent. We've sent your request for ", entry.name, " to the Livable team. You haven't been upgraded yet: they'll update your subscription and follow up.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.tierBox
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.tipTitle
  }, entry.name), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, entry.tip)), /*#__PURE__*/React.createElement("div", {
    className: classes.terms
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "It applies to your whole portfolio: every property your company has now, and every property you add later. Once it's active, your setups apply to every utility linked to them, and bills held on the Bills page are read."), entry.name === 'Plus' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "Plus can start with a 30-day free trial: the Livable team applies the trial coupon when it updates your subscription.")), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: agreed,
      onChange: event => setAgreed(event.target.checked),
      disabled: sending
    }),
    label: "I agree that ".concat(entry.name, " covers the whole portfolio.")
  }), state === 'failed' && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "We couldn't send your request.", ' ', /*#__PURE__*/React.createElement(Link, {
    href: SUBSCRIPTION_FORM_URL,
    target: "_blank",
    rel: "noopener noreferrer"
  }, "Open the request form"), ' ', "instead."))));
});